import React, { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { RootStoreContext } from '../../stores/rootStore';
import { observer } from "mobx-react-lite";
import { saveVisit } from '../../api/visitTrackerService';
import spinner from '../../assets/images/Spinner-1s-55px.gif';
import Maintenance from './maintenance';
import { PAYMENTS_DISABLED } from '../../constants';

//The page renders exactly the server-resolved renewal mode (renewStore.renewalMode):
//legacy / resubscribe = the classic order summary ending in Stripe Checkout;
//renewOrResume = resume auto-renewal on the existing Stripe subscription or update the card;
//payInvoice = pay the failed invoice on Stripe's hosted page (or update the card);
//autoRenewing = nothing to pay, offer updating the card; needsAttention = contact support.
const RenewSubscription = observer(() => {
    const { subscriptionCode, activationCodeEncoded } = useParams();
    const rootStore = useContext(RootStoreContext);
    const renewStore = rootStore.renewStore;
    const userStore = rootStore.userStore;
    const { loading } = renewStore;
    const curr = renewStore.currency === 'EUR' ? '€' : '£';
    const isLoggedIn = userStore.isLoggedIn;
    const periodLabel = renewStore.isMonthly ? '1 month' : '12 months';

    //isLoggedIn is a dependency on purpose: a Stripe-linked subscription answers 401 until the
    //owner signs in, and the page must refetch (landing in the right mode) right after login
    useEffect(() => {
        if (subscriptionCode) {
            renewStore.setSubscription(subscriptionCode);
            saveVisit('RenewSubscription: ' + renewStore.subscriptionId);
        } else if (activationCodeEncoded) {
            renewStore.setSubscriptionByActivationCode(activationCodeEncoded);
            saveVisit('RenewSubscription by activationCodeEncoded: ' + renewStore.subscriptionId);
        }
    }, [subscriptionCode, activationCodeEncoded, isLoggedIn]);

    const handlePromoInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        renewStore.setDiscountCode(e.target.value);
    };

    if (PAYMENTS_DISABLED || renewStore.paymentProvider === 'maintenance') return <Maintenance />;
    if (loading || !renewStore.paymentProvider) return <div className="page-center"><p>Loading…</p></div>;

    //this subscription is managed through Stripe: renewal details are for its signed-in owner
    if (renewStore.signInRequired && !isLoggedIn) {
        userStore.OpenLoginForm();
        return <div className="page-center"><p>Please sign in to manage this subscription's renewal.</p></div>;
    }
    if (!renewStore.renewalMode) return <div className="page-center"><p>Loading…</p></div>;

    const header = (
        <div className="order-head">
            <img src="/logo.svg" alt="" />
            <div>
                <div className="order-head-title">Desktop Call Recorder Pro</div>
                <div className="order-head-sub">
                    {renewStore.isNotExpiredYet
                        ? <>Expires <strong>{renewStore.endDate}</strong></>
                        : <>Expired <strong>{renewStore.endDate}</strong></>
                    }
                </div>
            </div>
        </div>
    );

    const actionError = renewStore.actionError && <span className="dcr-error">{renewStore.actionError}</span>;
    const busy = renewStore.actionBusy;

    //---- auto-renewing: nothing to pay; offer updating the payment method ----
    if (renewStore.renewalMode === 'autoRenewing') {
        return (
            <div className="order-wrap">
                <h1 className="page-title">Your subscription renews automatically</h1>
                <div className="order-card">
                    {header}
                    <div className="order-body">
                        <p className="checkout-note">
                            No payment is needed — your subscription renews by itself
                            {renewStore.nextChargeDate && <> on <strong>{renewStore.nextChargeDate}</strong></>} using
                            your saved payment method.
                        </p>
                    </div>
                    <div className="checkout-actions">
                        {actionError}
                        <button type="button" className="btn btn-primary" disabled={busy}
                            onClick={() => renewStore.updatePaymentMethod()}>
                            {busy ? 'Opening…' : 'Update payment details'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    //---- scheduled to end: turn auto-renewal back on, or update the payment method ----
    if (renewStore.renewalMode === 'renewOrResume') {
        return (
            <div className="order-wrap">
                <h1 className="page-title">Renew your subscription</h1>
                <div className="order-card">
                    {header}
                    <div className="order-body">
                        <div className="row">
                            <span className="row-label">PRO subscription <small>{renewStore.isMonthly ? 'monthly' : 'annual'}</small></span>
                            <span className="row-val">{renewStore.qnt} computer{renewStore.qnt > 1 ? 's' : ''}</span>
                        </div>
                        <div className="row">
                            <span className="row-label">Price per {renewStore.isMonthly ? 'month' : 'year'}</span>
                            <span className="row-val">{curr} {renewStore.totalString}</span>
                        </div>
                    </div>
                    <p className="checkout-note">
                        Auto-renewal is off, so your subscription is set to end. Turn it back on to keep
                        Pro active — nothing is charged today; your saved payment method is charged
                        {' '}{curr}{renewStore.totalString} on <strong>{renewStore.nextChargeDate || renewStore.endDate}</strong> and
                        then every {renewStore.isMonthly ? 'month' : 'year'} until you cancel.
                    </p>
                    <div className="checkout-actions">
                        {actionError}
                        {busy ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--muted)', fontSize: '.9rem' }}>
                                <img src={spinner} alt="" style={{ height: '28px' }} />
                                One moment…
                            </span>
                        ) : (
                            <>
                                <button type="button" className="btn btn-primary" onClick={() => renewStore.resumeAutoRenew()}>
                                    Turn on auto-renewal
                                </button>
                                <button type="button" className="btn" onClick={() => renewStore.updatePaymentMethod()}>
                                    Update payment details
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    //---- a renewal payment failed: settle the open invoice on Stripe's hosted page ----
    if (renewStore.renewalMode === 'payInvoice') {
        return (
            <div className="order-wrap">
                <h1 className="page-title">There's a payment problem</h1>
                <div className="order-card">
                    {header}
                    <div className="order-body">
                        <p className="checkout-note">
                            The renewal payment for your subscription didn't go through. You can pay the
                            open invoice now on Stripe's secure page — a different card can be used there.
                            Future renewals charge the card saved on your subscription, so update your
                            payment details if that card has changed.
                        </p>
                    </div>
                    <div className="checkout-actions">
                        {actionError}
                        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => renewStore.payInvoice()}>
                            Pay now
                        </button>
                        <button type="button" className="btn" disabled={busy} onClick={() => renewStore.updatePaymentMethod()}>
                            {busy ? 'Opening…' : 'Update payment details'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    //---- unrecognized billing state: never sell anything, point at support ----
    if (renewStore.renewalMode === 'needsAttention') {
        return (
            <div className="order-wrap">
                <h1 className="page-title">Renewal needs a quick check</h1>
                <div className="order-card">
                    {header}
                    <div className="order-body">
                        <p className="checkout-note">
                            We can't renew this subscription automatically right now. Please contact
                            support and we'll sort it out.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    //---- legacy / resubscribe: the classic order summary ending in Stripe Checkout ----
    return (
        <div className="order-wrap">
            <h1 className="page-title">Renew your subscription</h1>

            <div className="order-card">
                {header}

                <div className="order-body">
                    <div className="row">
                        <span className="row-label">
                            PRO subscription renewal
                            <small>× {periodLabel}</small>
                        </span>
                        <span className="row-val muted">{renewStore.isMonthly ? 'monthly' : 'annual'}</span>
                    </div>

                    <div className="row">
                        <span className="row-label">Number of computers</span>
                        <span className="row-val">{renewStore.qnt}</span>
                    </div>

                    <div className="row">
                        <span className="row-label">
                            Per computer per {renewStore.isMonthly ? 'month' : 'year'}
                            {!renewStore.isMonthly && <small>{curr}{renewStore.PricePerMonthString} × 12</small>}
                        </span>
                        <span className="row-val">{curr} {renewStore.pricePerUserString}</span>
                    </div>

                    {renewStore.earlyRenewalDiscountApplied && (
                        <div className="row">
                            <span className="row-label">Early renewal discount ({renewStore.renewalDiscountString}%)</span>
                            <span className="row-val discount">−{curr} {renewStore.renewalDiscountAmountString}</span>
                        </div>
                    )}

                    {renewStore.iHaveDiscount ? (
                        renewStore.isDiscountCodeValid ? (
                            <>
                                <div className="row">
                                    <span className="row-label">Discount code ({renewStore.discountPercentageString}%)</span>
                                    <span className="row-val discount">−{curr} {renewStore.discountCodeAmountString}</span>
                                </div>
                                <div className="row">
                                    <span className="row-label">Total savings</span>
                                    <span className="row-val discount">−{curr} {renewStore.discountCodeAmountString}</span>
                                </div>
                            </>
                        ) : (
                            <div className="row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
                                <span className="row-label">Promo code</span>
                                {renewStore.showCheckingDiscountSpinner ? (
                                    <span style={{ fontSize: '.84rem', color: 'var(--muted)' }}>Checking…</span>
                                ) : (
                                    <div className="discount-input">
                                        <input type="text" value={renewStore.discountCode} onChange={handlePromoInputChange} placeholder="Enter code" />
                                    </div>
                                )}
                                {renewStore.discountError && <span className="dcr-error">{renewStore.discountError}</span>}
                            </div>
                        )
                    ) : (
                        <div className="row">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '.84rem', color: 'var(--text2)', cursor: 'pointer', fontWeight: 500 }}>
                                <input type="checkbox" checked={renewStore.iHaveDiscount} onChange={e => renewStore.setIHaveDiscount(e.target.checked)} />
                                I have a discount code
                            </label>
                        </div>
                    )}
                    {renewStore.hasUnusedTimeCredit && (
                        <div className="row">
                            <span className="row-label">Unused time credit ({renewStore.unusedTimeCreditDays} days)</span>
                            <span className="row-val discount">−{curr} {renewStore.unusedTimeCreditString}</span>
                        </div>
                    )}
                </div>

                <div className="total-row">
                    <span className="row-label">Total</span>
                    <span className="row-val">{curr} {renewStore.totalAfterDiscountString}</span>
                </div>

                {renewStore.currency !== 'EUR' && renewStore.rootStore?.ratesLoaded && (
                    <p className="conv-line">≈ USD {renewStore.USDPriceString} or EUR {renewStore.EURPriceString}</p>
                )}

                <p className="checkout-note">
                    You'll be redirected to our secure payment provider to complete your purchase.
                    {renewStore.hasUnusedTimeCredit && <> Your remaining {renewStore.unusedTimeCreditDays} paid days are credited above, and your subscription will renew a year from today.</>}
                </p>

                {renewStore.paymentProvider === 'stripe' && !renewStore.managedPayments && (
                    <div className="invoice-section">
                        <label className="invoice-toggle">
                            <input type="checkbox" checked={renewStore.isInvoiceNeeded} onChange={e => renewStore.setInvoiceNeeded(e.target.checked)} />
                            I need an invoice
                        </label>

                        {renewStore.isInvoiceNeeded && (
                            <div className="invoice-fields">
                                <div className="full">
                                    <label>Name / Business name</label>
                                    <input className={renewStore.invoiceBusinessNameFieldStyle} type="text" value={renewStore.invoiceBusinessName} onChange={e => renewStore.setInvoiceBusinessName(e.target.value)} />
                                </div>
                                <div className="full">
                                    <label>Billing address</label>
                                    <textarea className={renewStore.invoiceAddressFieldStyle} rows={3} value={renewStore.invoiceAddress} onChange={e => renewStore.setInvoiceAddress(e.target.value)} />
                                </div>
                                <div>
                                    <label>Country (from your location)</label>
                                    <input type="text" value={renewStore.countries?.find(c => c.code === renewStore.invoiceCountryIsoCode)?.name ?? renewStore.invoiceCountryIsoCode} readOnly disabled />
                                </div>
                                <div>
                                    <label>Your reference (optional)</label>
                                    <input type="text" value={renewStore.invoiceRefNumber} onChange={e => renewStore.setInvoiceRefNumber(e.target.value)} />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="checkout-actions">
                    {renewStore.PurchaseError && (
                        <span className="dcr-error">{renewStore.PurchaseError}</span>
                    )}
                    {renewStore.isCheckoutClicked ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--muted)', fontSize: '.9rem' }}>
                            <img src={spinner} alt="" style={{ height: '28px' }} />
                            Opening secure checkout…
                        </span>
                    ) : (
                        <button type="button" className="btn btn-primary" onClick={() => renewStore.HandleCheckoutClicked()}>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
                            </svg>
                            Checkout
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
});

export default RenewSubscription;
